import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import { Plus, Pencil, Trash2, LayoutGrid, List, Upload } from 'lucide-react';
import { formatPrice, formatQty, formatPercent, round2 } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import ProductsGrid from '@/components/ProductsGrid';
import BulkProductImport from '@/components/BulkProductImport';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const calcCustomerDiscount = (vendorDisc, margin) => {
  if (margin >= 100) return 0;
  return 100 - ((100 - vendorDisc) / (1 - margin / 100));
};

const calcPrice = (mrp, discount, gst) => {
  return mrp * (1 - discount / 100) * (1 + gst / 100);
};

const Products = () => {
  const { user } = useOutletContext();
  const isAdmin = user?.role === 'admin';

  const [products, setProducts] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [priceFilter, setPriceFilter] = useState('all'); // all | input_zero | selling_zero | either_zero
  const [activeFilter, setActiveFilter] = useState('all'); // all | active | inactive
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('products_view_mode') || 'table');

  useEffect(() => { localStorage.setItem('products_view_mode', viewMode); }, [viewMode]);

  const onProductUpdated = useCallback((updated) => {
    setProducts(prev => prev.map(p => p.product_id === updated.product_id ? updated : p));
  }, []);

  const defaultForm = {
    product_name: '', product_brand: '', product_size: '', product_color: '',
    vendor_id: '', category: '', unit: '',
    input_price: 0, selling_price: 0,
    mrp: 0, vendor_discount_percent: 0, gst_percent: 0, margin_percent: 0,
    is_active: true,
    image_urls: [],
    publish_to_store: false,
  };
  const [formData, setFormData] = useState(defaultForm);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset so uploading the same file twice still fires
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image is larger than 8 MB. Please compress it and try again.');
      return;
    }
    setUploadingImage(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${BACKEND_URL}/api/products/upload-image`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Backend returns a relative URL like `/api/products/images/pimg_xxx`.
      // We store it as-is; the storefront + admin preview prefix it with BACKEND_URL at render time.
      const url = res.data.url;
      updateField('image_urls', [...(formData.image_urls || []), url]);
      toast.success('Image uploaded');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Upload failed. Please try again.';
      toast.error(msg);
    } finally {
      setUploadingImage(false);
    }
  };

  // Derived live calculations
  const liveCustomerDiscount = formData.mrp > 0
    ? calcCustomerDiscount(formData.vendor_discount_percent, formData.margin_percent)
    : 0;
  // Pricing rules (mirrors backend):
  //  1. If MRP + vendor_discount + GST all > 0 → derive input_price (overrides user input).
  //  2. Otherwise → keep user-typed input_price.
  //  3. selling_price = input_price × (1 + margin/100) — always derived when input_price > 0.
  //  4. If input_price = 0 → keep user-typed selling_price (no derivation).
  const liveInputPrice = (formData.mrp > 0 && formData.vendor_discount_percent > 0 && formData.gst_percent > 0)
    ? calcPrice(formData.mrp, formData.vendor_discount_percent, formData.gst_percent)
    : (formData.input_price || 0);
  const liveSellingPrice = liveInputPrice > 0
    ? liveInputPrice * (1 + (formData.margin_percent || 0) / 100)
    : (formData.selling_price || 0);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [productsRes, vendorsRes, inventoryRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/products`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/vendors`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/inventory`, { withCredentials: true })
      ]);
      setProducts(productsRes.data);
      setVendors(vendorsRes.data);
      setInventory(inventoryRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanImages = (formData.image_urls || []).map(u => (u || '').trim()).filter(Boolean);
    const payload = {
      ...formData,
      input_price: liveInputPrice,
      selling_price: liveSellingPrice,
      image_urls: cleanImages,
      publish_to_store: !!formData.publish_to_store,
    };
    try {
      if (editingProduct) {
        await axios.put(`${BACKEND_URL}/api/products/${editingProduct.product_id}`, payload, { withCredentials: true });
        toast.success('Product updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/products`, payload, { withCredentials: true });
        toast.success('Product created');
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/products/${productId}`, { withCredentials: true });
      toast.success('Product deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      product_name: product.product_name,
      product_brand: product.product_brand,
      product_size: product.product_size,
      product_color: product.product_color,
      vendor_id: product.vendor_id,
      category: product.category,
      unit: product.unit,
      input_price: product.input_price || 0,
      selling_price: product.selling_price || 0,
      mrp: product.mrp || 0,
      vendor_discount_percent: product.vendor_discount_percent || 0,
      gst_percent: product.gst_percent || 0,
      margin_percent: product.margin_percent || 0,
      is_active: product.is_active !== false,
      image_urls: Array.isArray(product.image_urls) ? [...product.image_urls] : [],
      publish_to_store: !!product.publish_to_store,
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingProduct(null);
    setFormData(defaultForm);
  };

  const getVendorName = (vendorId) => vendors.find(v => v.vendor_id === vendorId)?.vendor_name || 'Unknown';
  const getProductStock = (productId) => inventory.find(i => i.product_id === productId)?.current_stock || 0;

  const filteredProducts = products.filter(p => {
    // Active/Inactive filter
    if (activeFilter === 'active' && p.is_active === false) return false;
    if (activeFilter === 'inactive' && p.is_active !== false) return false;

    // Price-gap filter
    const ip = p.input_price || 0;
    const sp = p.selling_price || 0;
    if (priceFilter === 'input_zero' && ip > 0) return false;
    if (priceFilter === 'selling_zero' && sp > 0) return false;
    if (priceFilter === 'either_zero' && ip > 0 && sp > 0) return false;

    if (!searchFilter) return true;
    const keywords = searchFilter.toLowerCase().trim().split(/\s+/);
    const str = `${p.product_name} ${p.product_brand} ${p.product_size} ${p.product_color} ${p.category} ${getVendorName(p.vendor_id)}`.toLowerCase();
    return keywords.every(kw => str.includes(kw));
  });

  const missingPriceCount = products.filter(p => (p.input_price || 0) <= 0 || (p.selling_price || 0) <= 0).length;
  const inactiveCount = products.filter(p => p.is_active === false).length;

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const updateNumField = (field, value) => setFormData(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
  // Money / percent inputs: cap at 2 decimals while typing.
  // Allows blank, "12", "12.", "12.3", "12.34" but blocks "12.345".
  const TWO_DP_RE = /^\d*\.?\d{0,2}$/;
  const updateDecimalField = (field, value) => {
    if (value === '' || TWO_DP_RE.test(value)) {
      setFormData(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
    }
  };

  if (loading) return <div className="text-stone-600">Loading products...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Products</h1>
          <p className="mt-2 text-base text-stone-600">Manage your product catalog</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <div className="inline-flex rounded-md border border-stone-200 bg-white p-0.5 mr-1">
              <button
                data-testid="view-toggle-table"
                onClick={() => setViewMode('table')}
                className={`px-2.5 py-1 rounded text-xs flex items-center gap-1 transition ${viewMode === 'table' ? 'bg-stone-800 text-white' : 'text-stone-600 hover:bg-stone-50'}`}
                title="Compact table view"
              >
                <List className="w-3 h-3" /> Table
              </button>
              <button
                data-testid="view-toggle-grid"
                onClick={() => setViewMode('grid')}
                className={`px-2.5 py-1 rounded text-xs flex items-center gap-1 transition ${viewMode === 'grid' ? 'bg-stone-800 text-white' : 'text-stone-600 hover:bg-stone-50'}`}
                title="Edit-in-place grid view"
              >
                <LayoutGrid className="w-3 h-3" /> Grid (edit)
              </button>
            </div>
          )}
          {isAdmin && (
            <Button
              data-testid="bulk-import-btn"
              variant="outline"
              onClick={() => setBulkOpen(true)}
              className="border-stone-300"
            >
              <Upload className="w-4 h-4 mr-2" /> Add Many
            </Button>
          )}
          {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-product-btn" className="bg-amber-700 hover:bg-amber-800 text-white">
                <Plus className="w-4 h-4 mr-2" /> Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Basic info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Product Name *</Label>
                    <Input data-testid="product-name-input" value={formData.product_name} onChange={(e) => updateField('product_name', e.target.value)} required className="border-stone-200" />
                  </div>
                  <div>
                    <Label>Brand *</Label>
                    <Input data-testid="product-brand-input" value={formData.product_brand} onChange={(e) => updateField('product_brand', e.target.value)} required className="border-stone-200" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Size *</Label>
                    <Input data-testid="product-size-input" value={formData.product_size} onChange={(e) => updateField('product_size', e.target.value)} required className="border-stone-200" />
                  </div>
                  <div>
                    <Label>Color *</Label>
                    <Input data-testid="product-color-input" value={formData.product_color} onChange={(e) => updateField('product_color', e.target.value)} required className="border-stone-200" />
                  </div>
                </div>
                <div>
                  <Label>Vendor *</Label>
                  <Select value={formData.vendor_id} onValueChange={(v) => updateField('vendor_id', v)}>
                    <SelectTrigger data-testid="product-vendor-select" className="border-stone-200"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                    <SelectContent>{vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Category *</Label>
                    <Input data-testid="product-category-input" value={formData.category} onChange={(e) => updateField('category', e.target.value)} required placeholder="e.g., plywood, laminate" className="border-stone-200" />
                  </div>
                  <div>
                    <Label>Unit *</Label>
                    <Input data-testid="product-unit-input" value={formData.unit} onChange={(e) => updateField('unit', e.target.value)} required placeholder="e.g., sqft, nos, sheets" className="border-stone-200" />
                  </div>
                </div>

                {/* Pricing Section */}
                <div className="border-t border-stone-200 pt-4">
                  <h3 className="text-sm font-semibold text-stone-900 mb-3">Pricing</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>MRP</Label>
                      <Input data-testid="product-mrp-input" type="number" step="0.01" min="0"
                        value={formData.mrp}
                        onChange={(e) => updateDecimalField('mrp', e.target.value)}
                        className="border-stone-200" />
                    </div>
                    <div>
                      <Label>Vendor Discount %</Label>
                      <Input data-testid="product-vendor-discount-input" type="number" step="0.01" min="0" max="100" value={formData.vendor_discount_percent} onChange={(e) => updateDecimalField('vendor_discount_percent', e.target.value)} className="border-stone-200" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label>GST %</Label>
                      <Input data-testid="product-gst-input" type="number" step="0.01" min="0" value={formData.gst_percent} onChange={(e) => updateDecimalField('gst_percent', e.target.value)} className="border-stone-200" />
                    </div>
                    <div>
                      <Label>Input Price * {(formData.mrp > 0 && formData.vendor_discount_percent > 0 && formData.gst_percent > 0) && <span className="text-[10px] text-lime-600 font-normal ml-1">(auto-calculated)</span>}</Label>
                      <Input data-testid="product-input-price" type="number" step="0.01" min="0"
                        value={round2(liveInputPrice)}
                        onChange={(e) => updateDecimalField('input_price', e.target.value)}
                        readOnly={formData.mrp > 0 && formData.vendor_discount_percent > 0 && formData.gst_percent > 0}
                        required className={`border-stone-200 ${(formData.mrp > 0 && formData.vendor_discount_percent > 0 && formData.gst_percent > 0) ? 'bg-lime-50/50' : ''}`} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <Label>Desired Net Margin %</Label>
                      <Input data-testid="product-margin-input" type="number" step="0.01" min="0" value={formData.margin_percent} onChange={(e) => updateDecimalField('margin_percent', e.target.value)} className="border-stone-200" />
                    </div>
                    <div>
                      <Label>Selling Price * {(formData.mrp > 0 || (formData.input_price > 0 && formData.margin_percent > 0)) && <span className="text-[10px] text-lime-600 font-normal ml-1">(auto-calculated)</span>}</Label>
                      <Input data-testid="product-selling-price" type="number" step="0.01" min="0"
                        value={round2(liveSellingPrice)}
                        onChange={(e) => updateDecimalField('selling_price', e.target.value)}
                        readOnly={formData.mrp > 0 || (formData.input_price > 0 && formData.margin_percent > 0)}
                        required className={`border-stone-200 ${(formData.mrp > 0 || (formData.input_price > 0 && formData.margin_percent > 0)) ? 'bg-lime-50/50' : ''}`} />
                    </div>
                  </div>
                </div>

                {/* Active / Inactive toggle (soft-delete) */}
                <div className="border-t border-stone-200 pt-4 flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Label className="text-sm font-semibold text-stone-900">Active</Label>
                    <p className="text-xs text-stone-500 mt-1 leading-snug">
                      Inactive products are hidden from new Order &amp; Quotation dropdowns but
                      remain attached to historical records (so past invoices keep their
                      pricing context intact). Use this instead of deleting items you no
                      longer sell.
                    </p>
                  </div>
                  <Switch
                    data-testid="product-active-switch"
                    checked={!!formData.is_active}
                    onCheckedChange={(v) => updateField('is_active', v)}
                    className="mt-1"
                  />
                </div>

                {/* Publish to Storefront */}
                <div className="border-t border-stone-200 pt-4 flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <Label className="text-sm font-semibold text-stone-900">Publish on Storefront</Label>
                    <p className="text-xs text-stone-500 mt-1 leading-snug">
                      When ON, this product appears on the public shop at <code>nirmaanstore.com/shop</code>. Add at least one product image for the best look; text-only listings still work but show a placeholder tile.
                    </p>
                  </div>
                  <Switch
                    data-testid="product-publish-switch"
                    checked={!!formData.publish_to_store}
                    onCheckedChange={(v) => updateField('publish_to_store', v)}
                    className="mt-1"
                  />
                </div>

                {/* Product Images */}
                <div className="border-t border-stone-200 pt-4">
                  <Label className="text-sm font-semibold text-stone-900">Product Images</Label>
                  <p className="text-xs text-stone-500 mt-1 leading-snug mb-3">
                    Upload from your device, or paste image URLs. First image is used as the thumbnail. Up to 12 images per product. JPEG / PNG / WebP / GIF up to 8 MB each.
                  </p>
                  <div className="space-y-2">
                    {(formData.image_urls || []).map((url, idx) => (
                      <div key={idx} className="flex items-center gap-2" data-testid={`image-row-${idx}`}>
                        <div className="w-12 h-12 rounded overflow-hidden bg-stone-100 flex-shrink-0 border border-stone-200">
                          {url ? <img src={url.startsWith('http') ? url : `${BACKEND_URL}${url}`} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.opacity = 0.2; }} /> : null}
                        </div>
                        <Input
                          data-testid={`image-url-${idx}`}
                          value={url}
                          onChange={(e) => {
                            const next = [...formData.image_urls];
                            next[idx] = e.target.value;
                            updateField('image_urls', next);
                          }}
                          placeholder="https://… or /api/products/images/…"
                          className="border-stone-200 flex-1"
                        />
                        <button
                          type="button"
                          data-testid={`image-remove-${idx}`}
                          onClick={() => updateField('image_urls', formData.image_urls.filter((_, i) => i !== idx))}
                          className="text-red-600 hover:text-red-700 px-2 py-1 text-xs"
                          aria-label="Remove image"
                        >Remove</button>
                      </div>
                    ))}
                    {(formData.image_urls?.length || 0) < 12 && (
                      <div className="flex flex-wrap gap-2">
                        <label
                          data-testid="upload-image-btn"
                          className={`inline-flex items-center gap-1 text-xs rounded px-3 py-1.5 cursor-pointer transition ${uploadingImage ? 'opacity-50 pointer-events-none bg-stone-100 text-stone-500' : 'bg-amber-700 hover:bg-amber-800 text-white'}`}
                        >
                          {uploadingImage ? 'Uploading…' : '📤 Upload from device'}
                          <input
                            type="file"
                            accept="image/jpeg,image/png,image/webp,image/gif"
                            className="hidden"
                            disabled={uploadingImage}
                            onChange={handleImageUpload}
                          />
                        </label>
                        <button
                          type="button"
                          data-testid="add-image-btn"
                          onClick={() => updateField('image_urls', [...(formData.image_urls || []), ''])}
                          className="text-xs text-amber-700 hover:text-amber-800 border border-dashed border-amber-300 rounded px-3 py-1.5"
                        >+ Paste URL</button>
                      </div>
                    )}
                  </div>
                </div>

                <Button type="submit" data-testid="submit-product-btn" className="w-full bg-amber-700 hover:bg-amber-800 text-white">
                  {editingProduct ? 'Update Product' : 'Create Product'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {viewMode === 'grid' && isAdmin ? (
        <ProductsGrid products={products} vendors={vendors} onProductUpdated={onProductUpdated} />
      ) : (
      <>
      {/* Search + Price Filter */}
      <div className="bg-white border border-stone-200 rounded-md p-3 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <Input
          placeholder="Search products (name, brand, size, color, category, vendor)..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          className="text-xs h-8 border-stone-200 flex-1"
          data-testid="product-search-filter"
        />
        <Select value={activeFilter} onValueChange={setActiveFilter}>
          <SelectTrigger data-testid="product-active-filter" className="h-8 text-xs border-stone-200 w-full sm:w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="active-filter-all">All statuses</SelectItem>
            <SelectItem value="active" data-testid="active-filter-active">Active only</SelectItem>
            <SelectItem value="inactive" data-testid="active-filter-inactive">Inactive only{inactiveCount > 0 ? ` (${inactiveCount})` : ''}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priceFilter} onValueChange={setPriceFilter}>
          <SelectTrigger data-testid="product-price-filter" className="h-8 text-xs border-stone-200 w-full sm:w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="price-filter-all">All prices</SelectItem>
            <SelectItem value="either_zero" data-testid="price-filter-either-zero">Missing input or selling price{missingPriceCount > 0 ? ` (${missingPriceCount})` : ''}</SelectItem>
            <SelectItem value="input_zero" data-testid="price-filter-input-zero">Input price = 0</SelectItem>
            <SelectItem value="selling_zero" data-testid="price-filter-selling-zero">Selling price = 0</SelectItem>
          </SelectContent>
        </Select>
        {(priceFilter !== 'all' || activeFilter !== 'all') && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => { setPriceFilter('all'); setActiveFilter('all'); }}
            data-testid="price-filter-clear"
            className="h-8 text-xs text-stone-600 hover:text-stone-900"
          >
            Clear
          </Button>
        )}
      </div>

      <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Product</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Vendor</th>
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Stock</th>
                {/* Staff-conditional MRP column */}
                {!isAdmin && <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">MRP</th>}
                {isAdmin && <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">MRP</th>}
                {isAdmin && <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">V.Disc%</th>}
                {isAdmin && <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">GST%</th>}
                {isAdmin && <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Margin%</th>}
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Cust.Disc%</th>
                {isAdmin && <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Input</th>}
                <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Selling</th>
                {isAdmin && <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Profit</th>}
                {isAdmin && <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-stone-700">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200">
              {filteredProducts.length === 0 ? (
                <tr><td colSpan="20" className="px-6 py-8 text-center text-stone-500">{(searchFilter || priceFilter !== 'all' || activeFilter !== 'all') ? 'No products match your filters.' : 'No products found. Add your first product to get started.'}</td></tr>
              ) : (
                filteredProducts.map((product) => {
                  const stock = getProductStock(product.product_id);
                  const mrp = product.mrp || 0;
                  const vd = product.vendor_discount_percent || 0;
                  const cd = product.customer_discount_percent || 0;
                  const gst = product.gst_percent || 0;
                  // Staff sees MRP only when both discounts > 0
                  const staffShowMRP = vd > 0 && cd > 0;

                  return (
                    <tr key={product.product_id} data-testid={`product-row-${product.product_id}`} className="hover:bg-stone-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-stone-900 flex items-center gap-2">
                          <span>{product.product_name}</span>
                          {product.is_active === false && (
                            <span data-testid={`inactive-badge-${product.product_id}`} className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-stone-200 text-stone-600 font-semibold">Inactive</span>
                          )}
                          {product.publish_to_store && (
                            <span data-testid={`store-badge-${product.product_id}`} className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-lime-100 text-lime-800 font-semibold" title="Visible on public storefront">On Store</span>
                          )}
                        </div>
                        <div className="text-xs text-stone-500">{product.product_brand} | {product.product_size} | {product.product_color}</div>
                      </td>
                      <td className="px-4 py-3 text-stone-700">{getVendorName(product.vendor_id)}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        <span className={`font-semibold ${stock > 10 ? 'text-lime-700' : stock > 0 ? 'text-amber-700' : 'text-red-700'}`}>{formatQty(stock)}</span>
                      </td>
                      {/* MRP column */}
                      {!isAdmin && (
                        <td className="px-4 py-3 text-right tabular-nums text-stone-900">
                          {staffShowMRP && mrp > 0 ? formatPrice(mrp) : '-'}
                        </td>
                      )}
                      {isAdmin && <td className="px-4 py-3 text-right tabular-nums text-stone-900">{mrp > 0 ? formatPrice(mrp) : '-'}</td>}
                      {isAdmin && <td className="px-4 py-3 text-right tabular-nums text-stone-700">{vd > 0 ? `${vd}%` : '-'}</td>}
                      {isAdmin && <td className="px-4 py-3 text-right tabular-nums text-stone-700">{gst > 0 ? `${gst}%` : '-'}</td>}
                      {isAdmin && <td className="px-4 py-3 text-right tabular-nums text-stone-700">{(product.margin_percent || 0) > 0 ? `${product.margin_percent}%` : '-'}</td>}
                      <td className="px-4 py-3 text-right tabular-nums text-stone-700">{cd !== 0 ? formatPercent(cd) : '-'}</td>
                      {isAdmin && <td className="px-4 py-3 text-right tabular-nums text-stone-900">{formatPrice(product.input_price || 0)}</td>}
                      <td className="px-4 py-3 text-right tabular-nums text-stone-900 font-medium">{formatPrice(product.selling_price || 0)}</td>
                      {isAdmin && <td className="px-4 py-3 text-right tabular-nums font-medium text-lime-700">{formatPrice(product.profit_margin || 0)}</td>}
                      {isAdmin && (
                        <td className="px-4 py-3 text-right space-x-2">
                          <button data-testid={`edit-product-${product.product_id}`} onClick={() => openEditDialog(product)} className="text-amber-700 hover:text-amber-800"><Pencil className="w-4 h-4" /></button>
                          <button data-testid={`delete-product-${product.product_id}`} onClick={() => handleDelete(product.product_id)} className="text-red-700 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {isAdmin && (
        <BulkProductImport
          open={bulkOpen}
          onOpenChange={setBulkOpen}
          vendors={vendors}
          products={products}
          onImported={fetchData}
        />
      )}
    </div>
  );
};

export default Products;
